import { computed, signal } from '@preact/signals';
import type { SourceRegistryCard } from '../contracts/sources';
import type { ChaocEvent, EventCategory, EventSort } from '../contracts/events';
import type { PublicDataStatus } from './publicData';

export const eventsState = signal<ChaocEvent[]>([]);
export const sourcesState = signal<SourceRegistryCard[]>([]);
export const dataStatus = signal<PublicDataStatus>('loading');
export const dataError = signal<string | null>(null);

export const filterState = signal<EventCategory>('all');
export const sortState = signal<EventSort>('activity');
export const confidenceThresholdState = signal(0);
export const selectedEventIdState = signal<string | null>(null);
export const selectedHexIdState = signal<string | null>(null);
export const selectedHexEventsState = signal<ChaocEvent[]>([]);

export const commandPaletteOpenState = signal(false);
export const confidenceGraphOpenState = signal(false);
export const narrowPanelState = signal<'feed' | 'map' | 'detail'>('map');
export const mapResolutionState = signal('res-3');
export const clockState = signal('');
export const mapTargetState = signal<{ lng: number; lat: number; zoom: number } | null>(null);

function relativeAgeRank(timeAgo: string): number {
  const match = timeAgo.trim().toLowerCase().match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days|w|week|weeks)$/);
  if (!match) return Number.POSITIVE_INFINITY;

  const value = Number(match[1]);
  const unit = match[2];
  if (unit.startsWith('s')) return value;
  if (unit.startsWith('m')) return value * 60;
  if (unit.startsWith('h')) return value * 60 * 60;
  if (unit.startsWith('d')) return value * 60 * 60 * 24;
  if (unit.startsWith('w')) return value * 60 * 60 * 24 * 7;
  return Number.POSITIVE_INFINITY;
}

export const filteredEventsState = computed(() => {
  const threshold = confidenceThresholdState.value / 100;
  let items = eventsState.value.filter((event) => event.confidence >= threshold);
  const category = filterState.value;
  if (category !== 'all') items = items.filter((event) => event.category === category);

  if (sortState.value === 'activity') {
    items = [...items].sort((a, b) => b.activity - a.activity);
  } else if (sortState.value === 'confidence') {
    items = [...items].sort((a, b) => b.confidence - a.confidence);
  } else {
    items = [...items].sort((a, b) => relativeAgeRank(a.timeAgo) - relativeAgeRank(b.timeAgo));
  }
  return items;
});

export const selectedEventState = computed(() => {
  const id = selectedEventIdState.value;
  if (!id) return null;
  return eventsState.value.find((event) => event.id === id) || null;
});

export function selectEvent(event: ChaocEvent | null): void {
  selectedEventIdState.value = event?.id || null;
  selectedHexIdState.value = event ? `EVENT:${event.id}` : null;
  selectedHexEventsState.value = event ? [event] : [];
  if (event) {
    mapTargetState.value = { lng: event.lng, lat: event.lat, zoom: 6 };
    narrowPanelState.value = 'detail';
  }
}

export function selectHex(hexId: string, events: ChaocEvent[]): void {
  selectedHexIdState.value = hexId;
  selectedHexEventsState.value = events;
  selectedEventIdState.value = events[0]?.id || null;
  narrowPanelState.value = 'detail';
}
